import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Printer, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCompanyConfig } from "../../../hooks/useCompanyConfig";

/* ====== Theme helpers (ProductDetail ile aynı yaklaşım) ====== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#ffffff").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

type NavItem = { id: string; label: string };

const TeslimatDegisimIadeKosullari: React.FC = () => {
  const { theme } = useTheme();
  const company = useCompanyConfig();

  const accent = theme?.accent ?? "#2563eb";
  const textPrimary = theme?.textPrimary ?? "#0f172a";
  const textSecondary = theme?.textSecondary ?? "#475569";

  const pageBg = theme?.backgroundColor || "#ffffff";
  const bgRgb = hexToRgb(pageBg);
  const isDarkBg = luminance(bgRgb) < 0.5;

  const cardBg = isDarkBg ? "rgba(17,24,39,0.92)" : "#ffffff";
  const borderCol = isDarkBg ? "rgba(255,255,255,.14)" : "rgba(15,23,42,.12)";
  const softShadow = isDarkBg
    ? "0 10px 26px rgba(0,0,0,.35)"
    : "0 10px 26px rgba(2,6,23,.08)";

  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    borderColor: borderCol,
    borderWidth: 1,
    boxShadow: softShadow,
  };
  const subtleBorder: React.CSSProperties = { borderColor: borderCol };

  const lastUpdate = "23 Şubat 2026";

  const nav: NavItem[] = useMemo(
    () => [
      { id: "t1", label: "1. Teslimat" },
      { id: "t2", label: "2. Değişim & İade" },
      { id: "t3", label: "3. Garanti" },
      { id: "t4", label: "İletişim" },
    ],
    []
  );

  const onPrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div style={{ backgroundColor: pageBg }}>
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { padding: 0 !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          a { text-decoration: none !important; color: inherit !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-10 print-page">
        {/* Top bar */}
        <div className="no-print mb-4 md:mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
              style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
            >
              <ArrowLeft className="w-4 h-4" />
              Anasayfa
            </Link>

            <div className="hidden md:flex items-center gap-2 text-sm" style={{ color: textSecondary }}>
              <span className="opacity-70">/</span>
              <span>Teslimat, Değişim ve İade Koşulları</span>
            </div>
          </div>

          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
            style={{ ...subtleBorder, color: textPrimary, backgroundColor: cardBg }}
          >
            <Printer className="w-4 h-4" />
            Yazdır
          </button>
        </div>

        {/* Header */}
        <div className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div
                className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
                style={{
                  ...subtleBorder,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                <FileText className="w-4 h-4" />
                Politika Metni
              </div>

              <h1 className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight" style={{ color: textPrimary }}>
                TESLİMAT, DEĞİŞİM VE İADE KOŞULLARI
              </h1>

              <p className="mt-2 text-sm leading-6" style={{ color: textSecondary }}>
                <b style={{ color: textPrimary }}>Son Güncelleme:</b> {lastUpdate}
              </p>

              <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
                {company.companyName} olarak, alışveriş deneyiminizi en iyi şekilde tamamlamanız için şeffaf teslimat ve iade
                politikaları sunuyoruz. Aşağıdaki koşullar, <b style={{ color: textPrimary }}>{company.website}</b>{" "}
                adresinden yapacağınız tüm alışverişler için geçerlidir.
              </p>
            </div>

            <div
              className="hidden md:flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-2 border"
              style={{
                ...subtleBorder,
                color: textSecondary,
                backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
              }}
            >
              <ShieldCheck className="w-4 h-4" style={{ color: textPrimary }} />
              Şeffaf politika
            </div>
          </div>

          <div className="mt-5 border-t" style={subtleBorder} />

          {/* Quick nav */}
          <div className="no-print mt-4 flex flex-wrap gap-2 text-xs">
            {nav.map((x) => (
              <a
                key={x.id}
                href={`#${x.id}`}
                className="rounded-full border px-3 py-1.5 hover:opacity-90 transition"
                style={{
                  borderColor: borderCol,
                  color: textSecondary,
                  backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "#fff",
                }}
              >
                {x.label}
              </a>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-6 space-y-6">
          {/* 1 */}
          <section id="t1" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                1. TESLİMAT SÜREÇ VE KOŞULLARI
              </h2>
            </div>

            <div className="mt-4 space-y-5">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  1.1. Sipariş Onayı ve Hazırlama
                </div>
                <ul className="mt-2 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <li>Ödemenizin onaylanmasının ardından siparişiniz en geç 2 (iki) iş günü içinde kargoya verilmek üzere hazırlanır.</li>
                  <li>
                    Stokta olmayan ürünler için sizi e-posta veya telefon ile bilgilendirir, alternatif sunar veya tercihinize göre ödemenizi iade ederiz.
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  1.2. Kargo Seçenekleri ve Ücretleri
                </div>
                <ul className="mt-2 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <li>Anlaşmalı olduğumuz kargo firmaları (Yurtiçi Kargo, Aras Kargo, MNG Kargo vb.) aracılığıyla gönderim yapılmaktadır.</li>
                  <li>
                    <b style={{ color: textPrimary }}>1500 TL ve üzeri</b> tüm siparişlerde <b style={{ color: textPrimary }}>KARGO ÜCRETSİZDİR</b>.
                  </li>
                  <li>1500 TL altındaki siparişler için geçerli kargo ücreti, ödeme sayfasında size bildirilir.</li>
                  <li>
                    Kapıda ödeme seçeneği sunulmamaktadır. Tüm ödemeler siteniz üzerinden, güvenli ödeme sistemleriyle online alınmaktadır.
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  1.3. Teslimat Süresi
                </div>
                <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                  Siparişinizin hazırlanıp kargoya verilmesinden sonraki ortalama teslimat süresi <b style={{ color: textPrimary }}>1-3 iş günüdür</b>.
                  Bu süre, kargo firmasının yoğunluğuna, hafta sonuna denk gelmesine ve teslimat adresinizin konumuna göre değişiklik gösterebilir.
                  <div className="mt-3">
                    Özel günler (resmi tatiller, bayramlar, yoğun kampanya dönemleri) teslimat sürelerini uzatabilir. Bu durumda sizi bilgilendirmeye çalışırız.
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  1.4. Teslimat Kontrolü
                </div>
                <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
                  Kargo paketinizi teslim aldığınız anda kargo görevlisinin yanında hasar, ezilme, ıslaklık vb. kusurlar açısından mutlaka kontrol ediniz.
                  <div className="mt-3">
                    Pakette veya ürünlerde herhangi bir hasar/eksiklik tespit ederseniz, kargo tutanağına{" "}
                    <b style={{ color: textPrimary }}>"Ürün hasarlı/eksik teslim edildi"</b> şeklinde yazdırınız ve durumu en geç{" "}
                    <b style={{ color: textPrimary }}>1 (bir) gün</b> içinde{" "}
                    <b style={{ color: textPrimary }}>{company.mailDisplay}</b> adresine yazılı olarak ve fotoğrafla bildiriniz.
                    Aksi halde, taşıma sırasında oluşan hasarlardan sorumluluk kabul edemeyiz.
                  </div>
                </div>

                {/* Accent callout */}
                <div
                  className="mt-4 rounded-xl px-4 py-3 text-sm"
                  style={{
                    border: `1px solid ${borderCol}`,
                    backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "rgba(37,99,235,.06)",
                    color: textSecondary,
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="h-5 w-[3px] rounded-full" style={{ backgroundColor: accent as any }} />
                    Hasar bildirimi için fotoğraf eklemenizi öneririz.
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* 2 */}
          <section id="t2" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" style={{ color: textPrimary }} />
              <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
                2. DEĞİŞİM VE İADE KOŞULLARI
              </h2>
            </div>

            <p className="mt-3 text-sm leading-7" style={{ color: textSecondary }}>
              Tüm ürünleriniz, 6502 Sayılı Tüketicinin Korunması Kanunu kapsamında <b style={{ color: textPrimary }}>14 (on dört) günlük</b> yasal
              cayma hakkınıza tabidir.
            </p>

            <div className="mt-4 space-y-5">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  2.1. Cayma Hakkı Süresi ve Koşulları
                </div>
                <ul className="mt-2 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <li>Ürünü teslim aldığınız tarihten itibaren 14 gün içinde herhangi bir gerekçe göstermeksizin cayma hakkınızı kullanabilirsiniz.</li>
                  <li>
                    Cayma hakkını kullanmak için, bu süre içinde bizimle (<b style={{ color: textPrimary }}>{company.mailDisplay}</b>) iletişime geçerek
                    iade talebinizi bildirmeniz yeterlidir. Size iade için gerekli kargo bilgilerini ve iade formunu göndereceğiz.
                  </li>
                  <li>
                    Cayma hakkınızı kullanabilmeniz için, ürünün orijinal kutusu, ambalajı, kılavuzu ve faturası (veya fişi) ile birlikte, kullanılmamış,
                    denenmemiş, hasar görmemiş ve üzerinde herhangi bir değişiklik yapılmamış olarak iade edilmesi gerekmektedir.
                  </li>
                  <li>
                    Hijyen ve sağlık açısından açıldıktan sonra iadesi uygun olmayan ürünlerin (örneğin, açılmış, kısmen kullanılmış filament ruloları)
                    cayma hakkı kapsamında iadesi mümkün değildir. Sadece fabrika çıkış ambalajı bozulmamış ürünler iade edilebilir.
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  2.2. İade Kargo Ücreti
                </div>
                <ul className="mt-2 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <li>
                    Ürün herhangi bir satıcı hatasından (yanlış/hatalı ürün gönderimi, eksik gönderim vb.) kaynaklanıyorsa, iade kargo ücreti tarafımıza aittir.
                    Size ücretsiz iade etiketi sağlanacak veya kargo ücreti iadenize yansıtılacaktır.
                  </li>
                  <li>
                    Cayma hakkı kapsamında veya müşteri kaynaklı bir iade ise (ürünü beğenmeme, yanlış sipariş verme vb.), iade kargo ücreti alıcıya (size) aittir.
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  2.3. İade İşlem Süreci ve Para İadesi
                </div>

                <ol className="mt-2 list-decimal pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <li>İade talebinizi mail ile bildirin.</li>
                  <li>Size gönderilen talimatlara göre ürünü kargoya verin.</li>
                  <li>İade paketiniz tarafımıza ulaştığında, ürün kontrolümüz en geç 7 iş günü içinde tamamlanır.</li>
                  <li>
                    İade onaylandıktan sonra, ödemeniz 14 gün içinde aynı ödeme yönteminize (kredi kartı, havale vb.) iade edilir. Bankanızın işlem süreleri
                    nedeniyle para iadenizin hesabınıza yansıması birkaç iş günü daha sürebilir.
                  </li>
                </ol>

                <div
                  className="mt-4 rounded-xl px-4 py-3 text-sm"
                  style={{
                    border: `1px solid ${borderCol}`,
                    backgroundColor: isDarkBg ? "rgba(255,255,255,.04)" : "rgba(249,115,22,.08)",
                    color: textSecondary,
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="h-5 w-[3px] rounded-full" style={{ backgroundColor: "#f97316" }} />
                    İade talebinizi e-posta ile iletirken sipariş numaranızı eklemeniz süreci hızlandırır.
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-sm font-extrabold" style={{ color: textPrimary }}>
                  2.4. Arızalı veya Hatalı Ürün Değişimi
                </div>
                <ul className="mt-2 list-disc pl-5 space-y-2 text-sm leading-7" style={{ color: textSecondary }}>
                  <li>Ürün kullanım sırasında bir imalat hatası/defosu tespit ederseniz, ürünü teslim aldığınız tarihten itibaren 30 gün içinde bize bildirin.</li>
                  <li>
                    Arıza veya hata durumu tarafımızca onaylandığı takdirde, ürününüzü yenisiyle, ücretsiz olarak değiştiririz. Bu durumda tüm kargo masrafları
                    tarafımıza aittir.
                  </li>
                  <li>
                    Kullanıcı hatasından (yanlış baskı ayarları, uyumsuz yazıcı kullanımı, fiziksel darbe vb.) kaynaklanan arızalarda garanti kapsamı geçerli değildir.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3 */}
          <section id="t3" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              3. GARANTİ KOŞULLARI
            </h2>

            <div className="mt-2 text-sm leading-7" style={{ color: textSecondary }}>
              Satışını yaptığımız 3D yazıcı ve aksesuarlar, üretici firma garantisi kapsamındadır. Garanti süreleri ve koşulları ürüne özel olup ürün sayfasında belirtilir.
              <div className="mt-3">
                <b style={{ color: textPrimary }}>3D Filamentler:</b> Filamentler, nemden uzak, orijinal ambalajında saklandığı ve uygun yazıcı ayarlarında kullanıldığı sürece kalite
                garantisi altındadır. Nem almış, tıkanmaya veya kırılmaya neden olacak şekilde uygun olmayan koşullarda saklanmış filamentler garanti dışıdır.
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="t4" className="p-5 md:p-7 rounded-2xl border print-card" style={cardStyle}>
            <h2 className="text-lg md:text-xl font-bold" style={{ color: textPrimary }}>
              İletişim
            </h2>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  Marka
                </div>
                <div className="mt-1 font-extrabold" style={{ color: textPrimary }}>
                  {company.companyName}
                </div>
                <div className="mt-3 leading-7" style={{ color: textSecondary }}>
                  <div>
                    <b style={{ color: textPrimary }}>E-posta:</b> {company.mailDisplay}
                  </div>
                  <div>
                    <b style={{ color: textPrimary }}>Telefon:</b> {company.phoneDisplay}{" "}
                    <span className="text-xs opacity-80">(Mesai saatleri: Hafta içi 09:00-18:00)</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: borderCol }}>
                <div className="text-xs font-semibold" style={{ color: textSecondary }}>
                  Adres
                </div>
                <div className="mt-1 font-extrabold" style={{ color: textPrimary }}>
                  {company.addressLine}
                </div>
                <div className="mt-3 leading-7" style={{ color: textSecondary }}>
                  İade, değişim ve teslimat ile ilgili tüm sorularınız için bize e-posta ile ulaşabilirsiniz.
                </div>
              </div>
            </div>

            <div className="no-print mt-5 flex flex-wrap gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold"
                style={{ borderColor: borderCol, color: textPrimary, backgroundColor: cardBg }}
              >
                <ArrowLeft className="w-4 h-4" />
                Siteye dön
              </Link>
            </div>
          </section>

          {/* Accent footer strip */}
          <div
            className="h-1.5 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${String(accent)}, rgba(249,115,22,0.9))`,
              opacity: isDarkBg ? 0.9 : 1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TeslimatDegisimIadeKosullari;